import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState('checking');
  const [campaignName, setCampaignName] = useState('');
  const hasPolled = useRef(false);

  useEffect(() => {
    if (hasPolled.current) return;
    hasPolled.current = true;
    const sessionId = searchParams.get('session_id');
    if (!sessionId) { navigate('/dashboard'); return; }

    let attempts = 0;
    const pollStatus = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
        setCampaignName(res.data.campaign_name || '');
        if (res.data.payment_status === 'paid') { setStatus('success'); return; }
        if (res.data.status === 'expired') { setStatus('expired'); return; }
        attempts++;
        if (attempts >= 5) { setStatus('timeout'); return; }
        setTimeout(pollStatus, 2000);
      } catch { setStatus('error'); }
    };
    pollStatus();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="text-center max-w-md">
        {status === 'checking' && (
          <>
            <Loader2 size={40} className="text-[#002FA7] animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-['Cabinet_Grotesk'] font-bold mb-2 text-slate-900">Processing Payment</h1>
            <p className="text-slate-500 text-sm">Verifying your payment...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-emerald-50 border border-emerald-200 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={28} className="text-emerald-500" />
            </div>
            <h1 className="text-2xl font-['Cabinet_Grotesk'] font-bold mb-2 text-slate-900">Payment Successful!</h1>
            <p className="text-slate-500 text-sm mb-2">You are now enrolled in <span className="text-slate-800 font-medium">{campaignName}</span>.</p>
            <p className="text-slate-400 text-xs mb-8">Your campaign will be activated shortly.</p>
            <Button onClick={() => navigate('/dashboard')} className="bg-[#002FA7] text-white hover:bg-[#0040D6] rounded-full" data-testid="back-to-dashboard-btn">Go to Dashboard</Button>
          </>
        )}
        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <>
            <h1 className="text-2xl font-['Cabinet_Grotesk'] font-bold mb-2 text-slate-900">
              {status === 'expired' ? 'Session Expired' : 'Payment Status Unknown'}
            </h1>
            <p className="text-slate-500 text-sm mb-8">Please check your dashboard or try again.</p>
            <Button onClick={() => navigate('/dashboard')} className="bg-[#002FA7] text-white hover:bg-[#0040D6] rounded-full" data-testid="back-to-dashboard-btn">Go to Dashboard</Button>
          </>
        )}
      </div>
    </div>
  );
}
